import { Badge, Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { StriimDropdown, StriimTypography, StriimTooltip } from "@striim/striim-ui-v2";
import ConfigureDropdown from "./configure-dropdown";
import EditDropdown from "./edit-dropdown";
import ArrangementsDropdown, { getLayoutInfo } from "./arrangements.dropdown";
import api from "core/api/api";
import App from "app";
import growl from "app/components/common/growl";
import metaObjectStorage from "app/components/flow/designer/metaObjectStorage";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import appExporter from "app/components/flow/designer/appExporter";
import { useFetchAndPopulateNodes, useFlowLayout } from "../helpers/flow.hooks";
import { deleteNode, deleteMultipleNodes } from "../helpers/flow.utils";
import { Layouts } from "../helpers/flow.constants";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import navigateTo from "src/navigate-to";
import appListService from "../../../apps/pages/app-list/app-list-service";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    getAppModel,
    getCurrentFlowId,
    getClipboard,
    getCurrentLayout,
    getCurrentPlacementAlgorithm,
    getExceptionsCount as getExceptionsCountState,
    getNodes,
    getAppStatus,
    getIsAutomatedGroup,
    getIsMetadataBrowserOpen
} from "../helpers/selectors";
import {
    closeRightSidebarAction,
    closeAllSidebarsAction,
    copyNodeAction,
    cutNodeAction,
    deleteNodeAction,
    pasteClipboardNodeAction,
    setIsRightSidebarOpenAction,
    setIsExceptionsSidebarOpenAction,
    setIsTableProgressModalOpenAction,
    setShowAppSettingsSidebarAction,
    setIsDiscoverPIISidebarOpenAction,
    setIsSentinelLiveMonitorSidebarOpenAction,
    setExceptionsCountAction,
    setIsMetadataBrowserOpenAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { getExceptionsCount, deleteApp, useAutomatedGroup } from "../helpers/app.utils";
import statusManagement from "src/status-management";
import { isDuplicatable } from "../helpers/node.utils";

type ActionId =
    | "edit"
    | "layout"
    | "progress"
    | "exceptions"
    | "monitor"
    | "configure"
    | "insights"
    | "metadatabrowser";

const GAP_PX = 8;
const KEBAB_RESERVE_PX = 44;

const labeledButtonStyles = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    height: "32px",
    px: 1,
    borderRadius: "4px",
    cursor: "pointer",
    color: "greyscale.700",
    "&:hover": { background: "#EBF2FA" }
};

const iconStyles = { fontSize: 20, color: "greyscale.700" };

const layoutInfoTooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 280, "& span": { color: "white" } }}>
        <StriimTypography variant="caption3" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
            Flow Layout Options
        </StriimTypography>
        <StriimTypography variant="caption3" sx={{ display: "block", mb: 0.75 }}>
            <strong>Classic:</strong> Best for apps up to 50 components.
        </StriimTypography>
        <StriimTypography variant="caption3" sx={{ display: "block" }}>
            <strong>Enhanced:</strong> Best for larger apps. Supports vertical and horizontal layouts.
        </StriimTypography>
    </Box>
);

interface LabeledButtonProps {
    icon: React.ReactNode;
    tooltipComponent?: React.ReactNode;
    label: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    dataTestId?: string;
    showCaret?: boolean;
    badge?: number | null;
}

const LabeledButton = React.forwardRef<HTMLDivElement, LabeledButtonProps>(
    ({ icon, label, onClick, dataTestId, showCaret, badge, tooltipComponent = null }, ref) => (
        <Box ref={ref} component="div" onClick={onClick} data-test-id={dataTestId} sx={labeledButtonStyles}>
            {tooltipComponent}
            {icon}
            <StriimTypography variant="body3" color="greyscale.700" sx={{ whiteSpace: "nowrap" }}>
                {label}
            </StriimTypography>
            {badge ? (
                <Badge
                    badgeContent={badge}
                    color="error"
                    data-test-id="badge"
                    sx={{ ml: 0.5, "& .MuiBadge-badge": { position: "static", transform: "none" } }}
                />
            ) : null}
            {showCaret && <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "greyscale.700" }} />}
        </Box>
    )
);
LabeledButton.displayName = "LabeledButton";

interface ToolbarGraphActionsWrapperProps {
    appName?: string;
    flowName?: string;
    promptIfUnsavedChanged?: () => Promise<boolean>;
}

const ToolbarGraphActionsWrapper: React.FC<ToolbarGraphActionsWrapperProps> = ({
    appName,
    flowName,
    promptIfUnsavedChanged
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Partial<Record<ActionId, HTMLElement>>>({});
    const [containerWidth, setContainerWidth] = useState<number>(Number.POSITIVE_INFINITY);
    const [measuredWidths, setMeasuredWidths] = useState<Partial<Record<ActionId, number>>>({});

    const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
    const [arrangementsDropdownOpen, setArrangementsDropdownOpen] = useState(false);
    const [editDropdownOpen, setEditDropdownOpen] = useState(false);
    const [insightsDropdownOpen, setInsightsDropdownOpen] = useState(false);
    const [kebabAnchor, setKebabAnchor] = useState<null | HTMLElement>(null);
    const kebabOpen = Boolean(kebabAnchor);
    const closeKebab = useCallback(() => setKebabAnchor(null), []);

    const dispatch = useAppDispatch();
    const app = useAppSelector(getAppModel);
    const currentFlowId = useAppSelector(getCurrentFlowId);
    const clipboard = useAppSelector(getClipboard);
    const exceptionsCount = useAppSelector(getExceptionsCountState);
    const nodes = useAppSelector(getNodes);
    const appStatus = useAppSelector(getAppStatus);
    const isAutomatedGroup = useAppSelector(getIsAutomatedGroup);
    const isMetadataBrowserOpen = useAppSelector(getIsMetadataBrowserOpen);
    const currentLayout = useAppSelector(getCurrentLayout);
    const currentPlacementAlgorithm = useAppSelector(getCurrentPlacementAlgorithm);
    const group = useAutomatedGroup();

    const fetchAndPopulateNodes = useFetchAndPopulateNodes();
    const { onArrangementChange } = useFlowLayout();

    const appNameValue = app?.name ?? app?.get?.("name");
    const appNsName = app?.nsName ?? app?.get?.("nsName");
    const appId = app?.id ?? app?.get?.("id");
    const applicationDashboardId = app?.applicationDashboardId ?? app?.get?.("applicationDashboardId");

    const isAppCreated = !(
        statusManagement.isApplicationRunning(appStatus) ||
        statusManagement.isDeployedState(appStatus) ||
        statusManagement.isApplicationCrash(appStatus)
    );

    const selectedLayout = getLayoutInfo(currentPlacementAlgorithm, currentLayout);
    const effectiveSelection = (nodes ?? []).filter(n => n.selected);
    const hasSelection = effectiveSelection.length > 0;

    const disableRecompile = !statusManagement.isApplicationCreated(appStatus);
    const disableCut = !hasSelection;
    const disableCopy =
        !hasSelection || effectiveSelection.length > 1 || isDuplicatable(effectiveSelection[0], appStatus);
    const disableDelete = isAutomatedGroup ? !(hasSelection && appStatus === "CREATED") : appStatus !== "CREATED";
    const disablePaste = clipboard === null;

    // Poll for exceptions count every 30 seconds when app is loaded
    useEffect(() => {
        if (!app?.id) return;
        const fetchExceptionsCount = () => {
            getExceptionsCount(app).then(count => {
                dispatch(setExceptionsCountAction(count));
            });
        };
        fetchExceptionsCount();
        const pollingInterval = setInterval(fetchExceptionsCount, 30000);
        return () => clearInterval(pollingInterval);
    }, [app?.id, dispatch]);

    // Update exception count when app crashes
    useEffect(() => {
        if (app && appStatus && statusManagement.isApplicationCrash(appStatus)) {
            setTimeout(() => {
                getExceptionsCount(app).then(count => {
                    dispatch(setExceptionsCountAction(count));
                });
            }, 3000);
        }
    }, [appStatus, app, dispatch]);

    // Observe the container width to decide which actions stay inline vs. move into the kebab.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        setContainerWidth(el.clientWidth);
        const obs = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // Measure each visible action's natural width and cache it so the overflow calculation
    // is exact rather than relying on hand-tuned constants. Items that get pushed into the
    // kebab keep their cached width, so re-introducing them inline reuses the prior measurement.
    // Re-runs when anything that affects an item's width changes.
    useLayoutEffect(() => {
        const cache = itemRefs.current;
        setMeasuredWidths(prev => {
            let changed = false;
            const next = { ...prev };
            (Object.keys(cache) as ActionId[]).forEach(id => {
                const el = cache[id];
                if (!el) return;
                const w = el.offsetWidth;
                if (w > 0 && next[id] !== w) {
                    next[id] = w;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    });

    const handleCut = useCallback(() => {
        if (effectiveSelection.length === 0) return;
        dispatch(cutNodeAction(effectiveSelection));
        dispatch(closeRightSidebarAction({ appName, flowName }));
        const message =
            effectiveSelection.length > 1
                ? `${effectiveSelection.length} components cut successfully`
                : "Component cut successfully";
        growl.success(message, "Success", undefined, appNameValue);
    }, [effectiveSelection, dispatch, appNameValue, appName, flowName]);

    const handleCopy = useCallback(() => {
        if (effectiveSelection.length === 0) return;
        dispatch(copyNodeAction(effectiveSelection));
        effectiveSelection.forEach(n => {
            if (n.data?.metaObject) metaObjectStorage.save(n.data.metaObject);
        });
        dispatch(closeRightSidebarAction({ appName, flowName }));
        const message =
            effectiveSelection.length > 1
                ? `${effectiveSelection.length} components copied successfully`
                : "Component copied successfully";
        growl.success(message, "Success", undefined, appNameValue);
    }, [effectiveSelection, dispatch, appNameValue, appName, flowName]);

    const handleRecompile = useCallback(() => {
        if (appNameValue) {
            api.compile(appNameValue)
                .then((compilationResult: string) => {
                    growl.success(compilationResult, "Compilation Success", appNameValue, appNameValue);
                    fetchAndPopulateNodes();
                })
                .catch((error: any) => {
                    growl.error(error.message, "Compilation failed", appNameValue, appNameValue);
                });
        }
    }, [appNameValue, fetchAndPopulateNodes]);

    const handlePaste = useCallback(async () => {
        if (!clipboard || clipboard.nodes.length === 0) return;
        const isSameFlowCut = clipboard.isCut && clipboard.sourceFlowId === currentFlowId;
        dispatch(pasteClipboardNodeAction(String(Date.now())));
        const count = clipboard.nodes.length;
        const message = count > 1 ? `${count} components pasted successfully` : "Component pasted successfully";
        growl.success(message, "Success", undefined, appNameValue);
        if (clipboard.isCut && !isSameFlowCut) {
            // Mirror old designer's updateFlowId: save each component with the new flow before recompiling.
            // Without this, fetchAndPopulateNodes() after recompile re-reads the backend (which still has
            // the old flow ID) and the nodes disappear from the target flow.
            if (currentFlowId) {
                await Promise.all(
                    clipboard.nodes.map(async node => {
                        const metaObjectId = node.data?.metaObjectId;
                        if (!metaObjectId || metaObjectId === "temp-node") return;
                        try {
                            const model = await metaStoreService.findById(metaObjectId);
                            if (model) {
                                await model.save({ flow: MetaObjectConverter.getName(currentFlowId) });
                            }
                        } catch (error) {
                            console.error("Failed to update component flow on paste:", error);
                        }
                    })
                );
            }
            handleRecompile();
        }
    }, [clipboard, dispatch, appNameValue, handleRecompile, currentFlowId]);

    const handleDelete = useCallback(async () => {
        if (effectiveSelection.length === 0) {
            await deleteApp(app);
            return;
        }

        // Single-node delete keeps the original per-node confirmation modal owned by `deleteNode`.
        if (effectiveSelection.length === 1) {
            try {
                const result = await deleteNode(effectiveSelection[0], app, fetchAndPopulateNodes);
                if (result) {
                    dispatch(deleteNodeAction(result.nodeId));
                    dispatch(closeRightSidebarAction({ appName, flowName }));
                }
            } catch (error) {
                console.error("Delete node error:", error);
            }
            return;
        }

        await deleteMultipleNodes({
            selectedNodes: effectiveSelection,
            appModel: app,
            fetchAndPopulateNodes,
            dispatch,
            appName,
            flowName
        });
    }, [effectiveSelection, app, fetchAndPopulateNodes, dispatch, appName, flowName]);

    const handleOpenExceptions = useCallback(() => {
        if (app) {
            getExceptionsCount(app).then(count => {
                dispatch(setExceptionsCountAction(count));
            });
        }
        dispatch(closeRightSidebarAction({ appName, flowName }));
        dispatch(setIsExceptionsSidebarOpenAction(true));
    }, [dispatch, app, appName, flowName]);

    const handleProgress = useCallback(() => {
        dispatch(setIsTableProgressModalOpenAction(true));
    }, [dispatch]);

    const handleAppSettings = useCallback(async () => {
        if (!appNameValue) return;
        const canProceed = promptIfUnsavedChanged ? await promptIfUnsavedChanged() : true;
        if (!canProceed) return;
        dispatch(setIsRightSidebarOpenAction(false));
        dispatch(closeAllSidebarsAction({ appName, flowName }));
        dispatch(setShowAppSettingsSidebarAction(true));
    }, [appNameValue, promptIfUnsavedChanged, dispatch, appName, flowName]);

    const handleExport = useCallback(() => {
        if (appName) {
            appExporter.exportApplication(appName);
        }
    }, [appName]);

    const handleMonitor = useCallback(() => {
        if (appNsName && appNameValue) {
            const shortName = MetaObjectConverter.getShortId(appNsName, appNameValue);
            App.navigate(`#/monitor/${shortName}`, { trigger: true });
        }
    }, [appNsName, appNameValue]);

    const handleGoToAutomationPipeline = useCallback(() => {
        if (group) {
            appListService.setSelectedGroup(group);
            navigateTo.Apps();
        } else {
            console.warn("No group found for current app, cannot navigate to automation pipeline");
        }
    }, [group]);

    const handleViewValidationDashboard = useCallback(() => {
        if (applicationDashboardId) {
            App.navigate(`#/dashboard/view/${applicationDashboardId}`, { trigger: true });
        } else if (appId && appNameValue) {
            const dashboardName = `${appNameValue}_AppDashboard`;
            const result = api.createDataValidationDashboard(appId, dashboardName);
            if (result && typeof result.then === "function") {
                result
                    .then((dashboardID: string) => {
                        growl.success("Created Application Dashboard.", "Success", appNameValue, appNameValue);
                        App.navigate(`#/dashboard/view/${dashboardID}`, { trigger: true });
                    })
                    .catch((e: any) => {
                        growl.error(`Could not create dashboard: ${e.message}`, "Error", appNameValue, appNameValue);
                    });
            } else {
                growl.error("Could not create dashboard: API returned false", "Error", appNameValue, appNameValue);
            }
        }
    }, [applicationDashboardId, appId, appNameValue]);

    const handleDiscoverPII = useCallback(() => {
        dispatch(closeRightSidebarAction({ appName, flowName }));
        dispatch(closeAllSidebarsAction({ appName, flowName }));
        dispatch(setIsDiscoverPIISidebarOpenAction(true));
    }, [dispatch, appName, flowName]);

    const handleSentinelLiveMonitor = useCallback(() => {
        dispatch(closeRightSidebarAction({ appName, flowName }));
        dispatch(closeAllSidebarsAction({ appName, flowName }));
        dispatch(setIsSentinelLiveMonitorSidebarOpenAction(true));
    }, [dispatch, appName, flowName]);

    const handleMetadataBrowser = useCallback(() => {
        dispatch(closeRightSidebarAction({ appName, flowName }));
        dispatch(setIsMetadataBrowserOpenAction(!isMetadataBrowserOpen));
    }, [dispatch, appName, flowName, isMetadataBrowserOpen]);

    // ----- Priority + responsive split -----
    // Priority order: highest priority stays inline longest; lowest moves to kebab first.
    // CREATED: building the app — Edit / Layout / Configure are what the user reaches for.
    // Other statuses (running, deployed, crashed, ...): operations matter — Progress / Exceptions / Monitor.
    const priorityOrder = useMemo<ActionId[]>(() => {
        if (isAppCreated) {
            return ["edit", "layout", "configure", "metadatabrowser", "progress", "exceptions", "insights"];
        }
        return ["progress", "exceptions", "monitor", "layout", "configure", "insights"];
    }, [isAppCreated]);

    const { inlineIds, overflowIds } = useMemo(() => {
        // Until we've measured items at least once, render everything inline (in the visible
        // container) so the measurement layer can size them naturally. The useLayoutEffect runs
        // before paint, so this initial state is never painted.
        const allMeasured = priorityOrder.every(id => measuredWidths[id] != null);
        if (!Number.isFinite(containerWidth) || !allMeasured) {
            return { inlineIds: priorityOrder, overflowIds: [] as ActionId[] };
        }
        let total = 0;
        let cutoff = priorityOrder.length;
        for (let i = 0; i < priorityOrder.length; i++) {
            const id = priorityOrder[i];
            const itemWidth = (measuredWidths[id] as number) + (i > 0 ? GAP_PX : 0);
            const moreItemsAfter = i < priorityOrder.length - 1;
            const kebabReserve = moreItemsAfter ? KEBAB_RESERVE_PX + GAP_PX : 0;
            if (total + itemWidth + kebabReserve > containerWidth) {
                cutoff = i;
                break;
            }
            total += itemWidth;
        }
        return {
            inlineIds: priorityOrder.slice(0, cutoff),
            overflowIds: priorityOrder.slice(cutoff)
        };
    }, [containerWidth, priorityOrder, measuredWidths]);

    const showKebab = overflowIds.length > 0;

    // ----- Inline renderers -----
    const renderEditInline = () => (
        <StriimDropdown
            key="edit"
            extendOnClick
            closeOnSelect
            controlState={[editDropdownOpen, setEditDropdownOpen]}
            content={
                <EditDropdown
                    onCut={handleCut}
                    onCopy={handleCopy}
                    onPaste={handlePaste}
                    onDelete={handleDelete}
                    disableCut={disableCut}
                    disableCopy={disableCopy}
                    disablePaste={disablePaste}
                    disableDelete={disableDelete}
                    setEditDropdownOpen={setEditDropdownOpen}
                />
            }
            onClose={() => setEditDropdownOpen(false)}
        >
            <LabeledButton
                icon={<EditOutlinedIcon sx={iconStyles} />}
                label="Edit"
                showCaret
                onClick={() => setEditDropdownOpen(!editDropdownOpen)}
                dataTestId="action-edit"
            />
        </StriimDropdown>
    );

    const renderLayoutInline = () => (
        <StriimDropdown
            key="layout"
            extendOnClick
            closeOnSelect
            controlState={[arrangementsDropdownOpen, setArrangementsDropdownOpen]}
            content={<ArrangementsDropdown />}
            onClose={() => setArrangementsDropdownOpen(false)}
        >
            <LabeledButton
                icon={<Box sx={{ display: "inline-flex", alignItems: "center" }}>{selectedLayout.icon}</Box>}
                label="Layout"
                showCaret
                onClick={() => setArrangementsDropdownOpen(!arrangementsDropdownOpen)}
                dataTestId="action-autoLayout-dropdown"
                tooltipComponent={
                    <Tooltip title={layoutInfoTooltipContent} placement="bottom" arrow>
                        <InfoOutlinedIcon
                            data-test-id="action-autoLayout-info"
                            sx={{
                                fontSize: 16,
                                color: "greyscale.700"
                            }}
                        />
                    </Tooltip>
                }
            />
        </StriimDropdown>
    );

    const renderProgressInline = () => (
        <LabeledButton
            key="progress"
            icon={<ShowChartOutlinedIcon sx={iconStyles} />}
            label="Progress"
            onClick={handleProgress}
            dataTestId="action-progress"
        />
    );

    const renderExceptionsInline = () => (
        <LabeledButton
            key="exceptions"
            icon={<ReportProblemOutlinedIcon sx={iconStyles} />}
            label="Exceptions"
            onClick={handleOpenExceptions}
            dataTestId="exceptions"
            badge={exceptionsCount > 0 ? exceptionsCount : null}
        />
    );

    const renderMonitorInline = () => (
        <LabeledButton
            key="monitor"
            icon={<MonitorOutlinedIcon sx={iconStyles} />}
            label="Monitor"
            onClick={handleMonitor}
            dataTestId="action-monitor"
        />
    );

    const renderConfigureInline = () => (
        <StriimDropdown
            key="configure"
            extendOnClick
            closeOnSelect
            controlState={[configDropdownOpen, setConfigDropdownOpen]}
            content={
                <ConfigureDropdown
                    app={app}
                    onAppSettings={handleAppSettings}
                    onExport={handleExport}
                    onMonitor={handleMonitor}
                    onRecompile={handleRecompile}
                    onGoToAutomationPipeline={handleGoToAutomationPipeline}
                    onViewValidationDashboard={handleViewValidationDashboard}
                    isAutomatedGroup={isAutomatedGroup}
                    disableRecompile={disableRecompile}
                    setConfigDropdownOpen={setConfigDropdownOpen}
                />
            }
            onClose={() => setConfigDropdownOpen(false)}
        >
            <LabeledButton
                icon={<SettingsOutlinedIcon sx={iconStyles} />}
                label="Configure"
                showCaret
                onClick={() => setConfigDropdownOpen(!configDropdownOpen)}
                dataTestId="action-configuration"
            />
        </StriimDropdown>
    );

    const renderMetadataBrowserInline = () => (
        <LabeledButton
            key="metadatabrowser"
            icon={<StorageOutlinedIcon sx={iconStyles} />}
            label="Metadata Browser"
            onClick={handleMetadataBrowser}
            dataTestId="action-metadata-browser"
        />
    );

    const renderInsightsInline = () => (
        <StriimDropdown
            key="insights"
            extendOnClick
            closeOnSelect
            controlState={[insightsDropdownOpen, setInsightsDropdownOpen]}
            content={
                <Box sx={{ minWidth: 240, py: 0.5 }}>
                    <MenuItem
                        onClick={() => {
                            setInsightsDropdownOpen(false);
                            handleDiscoverPII();
                        }}
                    >
                        Sherlock AI
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setInsightsDropdownOpen(false);
                            handleSentinelLiveMonitor();
                        }}
                    >
                        Sentinel AI Activity
                    </MenuItem>
                </Box>
            }
            onClose={() => setInsightsDropdownOpen(false)}
        >
            <LabeledButton
                icon={<AutoAwesomeIcon sx={iconStyles} />}
                label="Insights"
                showCaret
                onClick={() => setInsightsDropdownOpen(!insightsDropdownOpen)}
                dataTestId="action-insights"
            />
        </StriimDropdown>
    );

    const inlineRenderers: Record<ActionId, () => React.ReactElement> = {
        edit: renderEditInline,
        metadatabrowser: renderMetadataBrowserInline,
        layout: renderLayoutInline,
        progress: renderProgressInline,
        exceptions: renderExceptionsInline,
        monitor: renderMonitorInline,
        configure: renderConfigureInline,
        insights: renderInsightsInline
    };

    // ----- Kebab section header style -----
    const sectionHeaderSx = {
        px: 1.5,
        pt: 1,
        pb: 0.5,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "greyscale.500"
    };

    // ----- Kebab Menu content (flattened sections) -----
    const renderKebabSections = () => {
        const sections: React.ReactNode[] = [];

        if (overflowIds.includes("edit")) {
            sections.push(
                <Box key="edit-header" sx={sectionHeaderSx}>
                    Edit
                </Box>
            );
            sections.push(
                <MenuItem
                    key="edit-cut"
                    disabled={disableCut}
                    onClick={() => {
                        closeKebab();
                        handleCut();
                    }}
                    data-test-id="option-Cut"
                >
                    Cut
                </MenuItem>,
                <MenuItem
                    key="edit-copy"
                    disabled={disableCopy}
                    onClick={() => {
                        closeKebab();
                        handleCopy();
                    }}
                    data-test-id="option-Copy"
                >
                    Copy
                </MenuItem>,
                <MenuItem
                    key="edit-paste"
                    disabled={disablePaste}
                    onClick={() => {
                        closeKebab();
                        handlePaste();
                    }}
                    data-test-id="option-Paste"
                >
                    Paste
                </MenuItem>,
                <MenuItem
                    key="edit-delete"
                    disabled={disableDelete}
                    onClick={() => {
                        closeKebab();
                        handleDelete();
                    }}
                    data-test-id="option-Delete"
                >
                    Delete
                </MenuItem>
            );
        }

        if (overflowIds.includes("layout")) {
            if (sections.length > 0) sections.push(<Divider key="layout-divider" sx={{ my: 0.5 }} />);
            const classic = getLayoutInfo("sugiyama", Layouts.VERTICAL);
            const enhancedH = getLayoutInfo("dagre", Layouts.HORIZONTAL);
            const enhancedV = getLayoutInfo("dagre", Layouts.VERTICAL);
            sections.push(
                <Box key="layout-header" sx={sectionHeaderSx}>
                    Layout
                </Box>
            );
            sections.push(
                <MenuItem
                    key="layout-classic"
                    onClick={() => {
                        closeKebab();
                        onArrangementChange("sugiyama", Layouts.VERTICAL);
                    }}
                >
                    <ListItemIcon>{classic.icon}</ListItemIcon>
                    <ListItemText>{classic.label}</ListItemText>
                </MenuItem>,
                <MenuItem
                    key="layout-eh"
                    onClick={() => {
                        closeKebab();
                        onArrangementChange("dagre", Layouts.HORIZONTAL);
                    }}
                >
                    <ListItemIcon>{enhancedH.icon}</ListItemIcon>
                    <ListItemText>{enhancedH.label}</ListItemText>
                </MenuItem>,
                <MenuItem
                    key="layout-ev"
                    onClick={() => {
                        closeKebab();
                        onArrangementChange("dagre", Layouts.VERTICAL);
                    }}
                >
                    <ListItemIcon>{enhancedV.icon}</ListItemIcon>
                    <ListItemText>{enhancedV.label}</ListItemText>
                </MenuItem>
            );
        }

        if (overflowIds.includes("progress")) {
            if (sections.length > 0) sections.push(<Divider key="progress-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <MenuItem
                    key="progress"
                    onClick={() => {
                        closeKebab();
                        handleProgress();
                    }}
                    data-test-id="action-progress"
                >
                    <ListItemIcon>
                        <ShowChartOutlinedIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Progress</ListItemText>
                </MenuItem>
            );
        }

        if (overflowIds.includes("exceptions")) {
            if (sections.length > 0 && !overflowIds.includes("progress"))
                sections.push(<Divider key="exceptions-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <MenuItem
                    key="exceptions"
                    onClick={() => {
                        closeKebab();
                        handleOpenExceptions();
                    }}
                    data-test-id="exceptions"
                >
                    <ListItemIcon>
                        <ReportProblemOutlinedIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Exceptions</ListItemText>
                    {exceptionsCount > 0 && (
                        <Badge badgeContent={exceptionsCount} color="error" sx={{ ml: 2, mr: 1 }} />
                    )}
                </MenuItem>
            );
        }

        if (overflowIds.includes("monitor")) {
            if (sections.length > 0 && !overflowIds.includes("exceptions") && !overflowIds.includes("progress"))
                sections.push(<Divider key="monitor-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <MenuItem
                    key="monitor"
                    onClick={() => {
                        closeKebab();
                        handleMonitor();
                    }}
                    data-test-id="action-monitor"
                >
                    <ListItemIcon>
                        <MonitorOutlinedIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Monitor</ListItemText>
                </MenuItem>
            );
        }

        if (overflowIds.includes("configure")) {
            sections.push(<Divider key="configure-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <Box key="configure-header" sx={sectionHeaderSx}>
                    Configure
                </Box>
            );
            sections.push(
                <MenuItem
                    key="cfg-settings"
                    onClick={() => {
                        closeKebab();
                        handleAppSettings();
                    }}
                    data-test-id="option-App settings"
                >
                    App settings
                </MenuItem>,
                <MenuItem
                    key="cfg-export"
                    onClick={() => {
                        closeKebab();
                        handleExport();
                    }}
                    data-test-id="option-Export"
                >
                    Export
                </MenuItem>,
                <MenuItem
                    key="cfg-monitor"
                    onClick={() => {
                        closeKebab();
                        handleMonitor();
                    }}
                    data-test-id="option-Monitor"
                >
                    Monitor
                </MenuItem>,
                <StriimTooltip
                    title={
                        disableRecompile
                            ? "Application should be in Created state to recompile"
                            : "Recompiles this application"
                    }
                >
                    <Box>
                        <MenuItem
                            key="cfg-recompile"
                            disabled={disableRecompile}
                            onClick={() => {
                                closeKebab();
                                handleRecompile();
                            }}
                            data-test-id="option-Recompile"
                        >
                            Recompile
                        </MenuItem>
                    </Box>
                </StriimTooltip>
            );
            if (isAutomatedGroup) {
                sections.push(
                    <MenuItem
                        key="cfg-pipeline"
                        onClick={() => {
                            closeKebab();
                            handleGoToAutomationPipeline();
                        }}
                        data-test-id="option-Automation-pipeline"
                    >
                        Go to the Automation Pipeline
                    </MenuItem>
                );
            }
            sections.push(
                <MenuItem
                    key="cfg-validation"
                    disabled={!app?.isDataValidationEnabled}
                    onClick={() => {
                        closeKebab();
                        handleViewValidationDashboard();
                    }}
                    data-test-id="option-Validation-dashboard"
                >
                    View Validation Dashboard
                </MenuItem>
            );
        }

        if (overflowIds.includes("metadatabrowser")) {
            if (sections.length > 0) sections.push(<Divider key="metadatabrowser-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <MenuItem
                    key="metadatabrowser"
                    onClick={() => {
                        closeKebab();
                        handleMetadataBrowser();
                    }}
                    data-test-id="action-metadata-browser"
                >
                    <ListItemIcon>
                        <StorageOutlinedIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Metadata Browser</ListItemText>
                </MenuItem>
            );
        }

        if (overflowIds.includes("insights")) {
            sections.push(<Divider key="insights-divider" sx={{ my: 0.5 }} />);
            sections.push(
                <Box key="insights-header" sx={sectionHeaderSx}>
                    Insights
                </Box>
            );
            sections.push(
                <MenuItem
                    key="insights-sherlock"
                    onClick={() => {
                        closeKebab();
                        handleDiscoverPII();
                    }}
                    data-test-id="ai-insights-discover"
                >
                    <ListItemIcon>
                        <AutoAwesomeIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Sherlock AI</ListItemText>
                </MenuItem>,
                <MenuItem
                    key="insights-sentinel"
                    onClick={() => {
                        closeKebab();
                        handleSentinelLiveMonitor();
                    }}
                    data-test-id="ai-insights-sentinel-monitor"
                >
                    <ListItemIcon>
                        <AutoAwesomeIcon sx={iconStyles} />
                    </ListItemIcon>
                    <ListItemText>Sentinel AI Activity</ListItemText>
                </MenuItem>
            );
        }

        return sections;
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: `${GAP_PX}px`,
                flex: "1 1 0",
                minWidth: 0,
                justifyContent: "flex-end",
                overflow: "hidden"
            }}
            data-test-id="flow-designer-toolbar-actions"
        >
            {inlineIds.map(id => (
                <Box
                    key={id}
                    ref={el => {
                        if (el) itemRefs.current[id] = el as HTMLElement;
                        else delete itemRefs.current[id];
                    }}
                    sx={{ display: "inline-flex", alignItems: "center" }}
                >
                    {inlineRenderers[id]()}
                </Box>
            ))}
            {showKebab && (
                <>
                    <IconButton
                        size="small"
                        data-test-id="toolbar-overflow"
                        onClick={e => setKebabAnchor(e.currentTarget)}
                        sx={{ flexShrink: 0 }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={kebabAnchor}
                        open={kebabOpen}
                        onClose={closeKebab}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{ sx: { minWidth: 240 } }}
                    >
                        {renderKebabSections()}
                    </Menu>
                </>
            )}
            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0, my: 0.5, borderColor: "greyscale.300" }} />
        </Box>
    );
};

export default ToolbarGraphActionsWrapper;
